/**
 * Multi-Device Provider
 * 다중 디바이스 WebRTC 스트리밍의 최상위 관리자
 * 여러 디바이스의 라이프사이클과 인증을 총괄 관리
 */

import { DeviceStreamManager } from './DeviceStreamManager';
import { createWebRTCSDKLoader, type ISDKLoader } from '../live-provider/SDKLoader';
import type {
  MultiDeviceProviderConfig,
  MultiDeviceProviderEvents,
  MultiDeviceStats,
  DeviceStats,
  StreamCallback,
  VoiceCommState,
  TwoWayAudioStatus,
  VoiceCommCallback
} from './types';
import { DEFAULT_MULTI_DEVICE_CONFIG } from './constants';

/**
 * Multi-Device Provider Class
 * 여러 디바이스의 스트리밍을 통합 관리하는 최상위 Provider
 */
export class MultiDeviceProvider {
  // Core configuration
  private config: MultiDeviceProviderConfig;
  private isInitialized: boolean = false;
  
  // SDK Loader
  private sdkLoader: ISDKLoader;
  
  // Device management
  private deviceManagers: Map<string, DeviceStreamManager> = new Map();
  
  // Statistics and monitoring
  private globalStats: MultiDeviceStats;
  private statsTimer: NodeJS.Timeout | null = null;
  
  // Event callbacks
  private events: MultiDeviceProviderEvents = {};
  
  /**
   * Create Multi-Device Provider instance
   * @param config - Provider configuration
   */
  constructor(config: Partial<MultiDeviceProviderConfig> = {}) {
    this.config = { 
      ...DEFAULT_MULTI_DEVICE_CONFIG, 
      maxDevices: 64, // Increase default max devices for 8x8 support
      ...config 
    };
    
    // Initialize SDK loader
    this.sdkLoader = createWebRTCSDKLoader();
    
    // Initialize global statistics
    this.globalStats = {
      totalDevices: 0,
      connectedDevices: 0,
      totalConnections: 0,
      totalBandwidth: 0,
      memoryUsage: 0,
      deviceStats: [],
      lastUpdated: Date.now(),
      activeVoiceComm: 0
    };
  }

  /**
   * Initialize the provider
   * 프로바이더 초기화 및 통계 모니터링 시작
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.warn('🚨 [MultiDeviceProvider] Already initialized');
      return;
    }
    
    try {
      // Load WebRTC SDK first
      await this.sdkLoader.loadSDK();
    } catch (error) {
      const errorMessage = `Failed to load WebRTC SDK: ${error instanceof Error ? error.message : 'Unknown error'}`;
      this.events.onError?.(new Error(errorMessage));
      throw new Error(errorMessage);
    }
    
    this.isInitialized = true;
    
    // Start statistics monitoring if enabled
    if (this.config.enableStatistics) {
      this.startStatisticsMonitoring();
    }
  }

  /**
   * Add a new device to the provider
   * 새 디바이스를 Provider에 추가
   * @param psn - Device PSN to add
   * @param initialChannel - Initial channel for the device
   * @returns Device stream manager instance
   */
  async addDevice(psn: string, initialChannel: import('./types').CameraChannel = 'front'): Promise<DeviceStreamManager> {
    if (this.deviceManagers.has(psn)) {
      return this.deviceManagers.get(psn)!;
    }
    
    if (this.deviceManagers.size >= this.config.maxDevices) {
      throw new Error(`Maximum device limit reached: ${this.config.maxDevices}`);
    }
    
    try {
      // Create device stream manager
      const manager = new DeviceStreamManager(psn, {
        initialChannel: initialChannel,
        defaultQuality: this.config.defaultQuality,
        enableConnectionPooling: this.config.enableConnectionPooling,
        connectionTimeout: this.config.connectionTimeout,
        enableVoiceCommSupport: this.config.enableVoiceCommSupport
      });
      
      // Set up device-level event handlers
      this.setupDeviceEventHandlers(psn, manager);
      
      // Initialize the device manager
      await manager.initialize();
      
      // Store the manager
      this.deviceManagers.set(psn, manager);
      
      // Update statistics
      this.updateGlobalStats();
      
      // Notify event callback
      this.events.onDeviceAdded?.(psn);
      return manager;
      
    } catch (error) {
      const errorMessage = `Failed to add device ${psn}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      const errorObj = error instanceof Error ? error : new Error(errorMessage);
      this.events.onError?.(errorObj);
      throw new Error(errorMessage);
    }
  }

  /**
   * Remove a device from the provider
   * 디바이스를 Provider에서 제거
   * @param psn - Device PSN to remove
   */
  async removeDevice(psn: string): Promise<void> {
    const manager = this.deviceManagers.get(psn);
    if (!manager) {
      return;
    }
    
    try {
      // Destroy the device manager
      await manager.destroy();
      
      // Remove from map
      this.deviceManagers.delete(psn);
      
      // Update statistics
      this.updateGlobalStats();
      
      // Notify event callback
      this.events.onDeviceRemoved?.(psn);
      
    } catch (error) {
      const errorMessage = `Failed to remove device ${psn}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      const errorObj = error instanceof Error ? error : new Error(errorMessage);
      this.events.onError?.(errorObj);
    }
  }

  /**
   * Get device manager for a specific PSN
   * 특정 PSN의 디바이스 매니저 조회
   * @param psn - Device PSN
   * @returns Device stream manager or null
   */
  getDeviceManager(psn: string): DeviceStreamManager | null {
    return this.deviceManagers.get(psn) || null;
  }

  /**
   * Get all device managers
   * 모든 디바이스 매니저 목록 조회
   * @returns Array of device stream managers
   */
  getAllDevices(): DeviceStreamManager[] {
    return Array.from(this.deviceManagers.values());
  }

  /**
   * Get list of all device PSNs
   * 모든 디바이스 PSN 목록 조회
   * @returns Array of device PSNs
   */
  getDevicePSNs(): string[] {
    return Array.from(this.deviceManagers.keys());
  }

  /**
   * Connect all devices
   * 모든 디바이스 연결
   */
  async connectAllDevices(): Promise<void> {
    const connectionPromises = Array.from(this.deviceManagers.values()).map(async (manager) => {
      try {
        await manager.connectMainStream();
      } catch (error) {
      }
    });
    
    await Promise.allSettled(connectionPromises);
  }

  /**
   * Disconnect all devices
   * 모든 디바이스 연결 해제
   */
  async disconnectAllDevices(): Promise<void> {
    const disconnectionPromises = Array.from(this.deviceManagers.values()).map(async (manager) => {
      try {
        await manager.disconnect();
      } catch (error) {
      }
    });
    
    await Promise.allSettled(disconnectionPromises);
  }

  /**
   * Start voice communication for a specific device
   * 특정 디바이스의 음성 통신 시작
   * @param psn - Device PSN
   * @returns Promise with two-way audio status
   */
  async startVoiceComm(psn: string): Promise<TwoWayAudioStatus> {
    const manager = this.deviceManagers.get(psn);
    if (!manager) {
      throw new Error(`Device ${psn} not found`);
    }
    
    if (!this.config.enableVoiceCommSupport) {
      throw new Error('Voice communication support is disabled');
    }
    
    try {
      const status = await manager.startVoiceComm();
      this.updateGlobalStats();
      return status;
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Stop voice communication for a specific device
   * 특정 디바이스의 음성 통신 중지
   * @param psn - Device PSN
   * @returns Promise with two-way audio status
   */
  async stopVoiceComm(psn: string): Promise<TwoWayAudioStatus> {
    const manager = this.deviceManagers.get(psn);
    if (!manager) {
      throw new Error(`Device ${psn} not found`);
    }
    
    try {
      const status = await manager.stopVoiceComm();
      this.updateGlobalStats();
      return status;
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Stop all active voice communications
   * 모든 활성 음성 통신 중지
   */
  async stopAllVoiceComm(): Promise<void> {
    const stopPromises = Array.from(this.deviceManagers.values()).map(async (manager) => {
      const voiceState = manager.getVoiceCommState();
      if (voiceState.isActive) {
        try {
          await manager.stopVoiceComm();
        } catch (error) {
        }
      }
    });
    
    await Promise.allSettled(stopPromises);
  }
  
  /**
   * Set local audio enabled for a specific device
   * 특정 디바이스의 로컬 오디오 활성화/비활성화
   * @param psn - Device PSN
   * @param enabled - Audio enabled state
   */
  setLocalAudioEnabled(psn: string, enabled: boolean): void {
    const manager = this.deviceManagers.get(psn);
    if (!manager) {
      return;
    }
    
    manager.setLocalAudioEnabled(enabled);
  }
  
  /**
   * Set remote audio volume for a specific device
   * 특정 디바이스의 원격 오디오 볼륨 설정
   * @param psn - Device PSN
   * @param volume - Volume level (0.0 to 1.0)
   */
  setRemoteAudioVolume(psn: string, volume: number): void {
    const manager = this.deviceManagers.get(psn);
    if (!manager) {
      return;
    }
    
    manager.setRemoteAudioVolume(volume);
  }
  
  /**
   * Get voice communication state for a specific device
   * 특정 디바이스의 음성 통신 상태 조회
   * @param psn - Device PSN
   * @returns Voice communication state or null
   */
  getVoiceCommState(psn: string): VoiceCommState | null {
    const manager = this.deviceManagers.get(psn);
    if (!manager) {
      return null;
    }
    
    return manager.getVoiceCommState();
  }
  
  /**
   * Get global statistics
   * 전체 통계 정보 조회
   * @returns Global multi-device statistics
   */
  getGlobalStats(): MultiDeviceStats {
    return { ...this.globalStats };
  }

  /**
   * Set event callbacks
   * 이벤트 콜백 등록
   * @param events - Event callback functions
   */
  setEvents(events: MultiDeviceProviderEvents): void {
    this.events = { ...this.events, ...events };
  }

  /**
   * Update provider configuration
   * Provider 설정 업데이트
   * @param config - New configuration values
   */
  updateConfig(config: Partial<MultiDeviceProviderConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Restart statistics monitoring if interval changed
    if (config.statisticsInterval && this.config.enableStatistics) {
      this.stopStatisticsMonitoring();
      this.startStatisticsMonitoring();
    }
  }

  /**
   * Destroy provider and cleanup all resources
   * Provider 파괴 및 모든 리소스 정리
   */
  async destroy(): Promise<void> {
    // Stop statistics monitoring
    this.stopStatisticsMonitoring();
    
    // Destroy all device managers
    const destroyPromises = Array.from(this.deviceManagers.values()).map(async (manager) => {
      try {
        await manager.destroy();
      } catch (error) {
      }
    });
    
    await Promise.allSettled(destroyPromises);
    
    // Clear all data
    this.deviceManagers.clear();
    this.events = {};
    this.isInitialized = false;
  }

  // ========================
  // PRIVATE METHODS
  // ========================

  /**
   * Setup event handlers for a device manager
   * 디바이스 매니저의 이벤트 핸들러 설정
   * @param psn - Device PSN
   * @param manager - Device stream manager
   */
  private setupDeviceEventHandlers(psn: string, manager: DeviceStreamManager): void {
    manager.setEvents({
      onStreamUpdate: (event) => {
        // 글로벌 스트림 업데이트 이벤트로 전파할 수 있음
      },
      
      onConnectionStateChange: (psn, channel, state) => {
        this.updateGlobalStats();
      },
      
      onVoiceCommStateChange: (psn, voiceState) => {
        this.updateGlobalStats();
        this.events.onVoiceCommStateChange?.(psn, voiceState);
      },
      
      onChannelChanged: (psn, oldChannel, newChannel) => {
      },
      
      onError: (psn, error) => {
        this.events.onError?.(error);
      },
      
      onStatsUpdate: (psn, stats) => {
        // Device stats updated, trigger global stats update
        this.updateGlobalStats();
      }
    });
  }

  /**
   * Update global statistics
   * 전체 통계 업데이트
   */
  private updateGlobalStats(): void {
    const deviceStats: DeviceStats[] = [];
    let totalConnections = 0;
    let connectedDevices = 0;
    let totalBandwidth = 0;
    let totalMemoryUsage = 0;
    let activeVoiceComm = 0;
    
    this.deviceManagers.forEach((manager, psn) => {
      const stats = manager.getStats();
      deviceStats.push(stats);
      
      totalConnections += stats.activeConnections;
      totalBandwidth += stats.bandwidthUsage;
      totalMemoryUsage += stats.memoryUsage;
      
      // Count active voice communications
      if (stats.voiceCommState.isActive) {
        activeVoiceComm++;
      }
      
      // Check if device has any connected channels
      const hasConnectedChannels = Object.values(stats.connectionStates).some(state => state === 'connected');
      if (hasConnectedChannels) {
        connectedDevices++;
      }
    });
    
    this.globalStats = {
      totalDevices: this.deviceManagers.size,
      connectedDevices,
      totalConnections,
      totalBandwidth,
      memoryUsage: totalMemoryUsage,
      deviceStats,
      lastUpdated: Date.now(),
      activeVoiceComm
    };
    
    // Notify global stats update
    this.events.onGlobalStatsUpdate?.(this.globalStats);
  }

  /**
   * Start statistics monitoring
   * 통계 모니터링 시작
   */
  private startStatisticsMonitoring(): void {
    if (this.statsTimer) return;
    
    this.statsTimer = setInterval(() => {
      this.updateGlobalStats();
    }, this.config.statisticsInterval);
  }

  /**
   * Stop statistics monitoring
   * 통계 모니터링 중지
   */
  private stopStatisticsMonitoring(): void {
    if (this.statsTimer) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
  }
}